import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  createWeb3Modal,
  defaultConfig,
  useWeb3ModalAccount,
  useWeb3ModalProvider,
  useDisconnect,
  useSwitchNetwork,
  useWeb3Modal,
} from '@web3modal/ethers/react';
import { ethers } from 'ethers';

const WalletContext = createContext(null);

// WalletConnect Cloud project ID — get yours free at https://cloud.walletconnect.com
// Without a valid ID, QR code pairing won't work (browser extension wallets still work)
const WALLETCONNECT_PROJECT_ID = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || 'e899c82be21d4acca2c8aec45e893598';

const CHAINS = {
  1:     { name: 'Ethereum',  currency: 'ETH',   rpc: 'https://eth.llamarpc.com' },
  42161: { name: 'Arbitrum',  currency: 'ETH',   rpc: 'https://arb1.arbitrum.io/rpc' },
  8453:  { name: 'Base',      currency: 'ETH',   rpc: 'https://mainnet.base.org' },
  10:    { name: 'Optimism',  currency: 'ETH',   rpc: 'https://mainnet.optimism.io' },
  137:   { name: 'Polygon',   currency: 'MATIC', rpc: 'https://polygon-rpc.com' },
};

// Web3Modal chain definitions
const mainnet = {
  chainId: 1,
  name: 'Ethereum',
  currency: 'ETH',
  explorerUrl: 'https://etherscan.io',
  rpcUrl: 'https://eth.llamarpc.com',
};

const arbitrum = {
  chainId: 42161,
  name: 'Arbitrum',
  currency: 'ETH',
  explorerUrl: 'https://arbiscan.io',
  rpcUrl: 'https://arb1.arbitrum.io/rpc',
};

const base = {
  chainId: 8453,
  name: 'Base',
  currency: 'ETH',
  explorerUrl: 'https://basescan.org',
  rpcUrl: 'https://mainnet.base.org',
};

const optimism = {
  chainId: 10,
  name: 'Optimism',
  currency: 'ETH',
  explorerUrl: 'https://optimistic.etherscan.io',
  rpcUrl: 'https://mainnet.optimism.io',
};

const polygon = {
  chainId: 137,
  name: 'Polygon',
  currency: 'MATIC',
  explorerUrl: 'https://polygonscan.com',
  rpcUrl: 'https://polygon-rpc.com',
};

const chains = [mainnet, arbitrum, base, optimism, polygon];

const metadata = {
  name: 'PoolScope',
  description: 'DeFi pool aggregator & yield scanner',
  url: typeof window !== 'undefined' ? window.location.origin : 'https://poolscope.app',
  icons: ['https://poolscope.app/logo192.png'],
};

const ethersConfig = defaultConfig({ metadata });

// Initialize the modal once at module level — createWeb3Modal from the /react
// export registers internal valtio stores that the hooks read from, so it must
// be called before any component renders.
createWeb3Modal({
  ethersConfig,
  chains,
  projectId: WALLETCONNECT_PROJECT_ID,
  enableAnalytics: false,
  themeMode: 'light',
  features: {
    analytics: false,
    email: false,
    socials: false,
  },
  featuredWalletIds: [
    'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // MetaMask
    'fd20dc426fb37566d803205b19bbc1d4096b248ac04548e18e4a0726b2222f39', // Coinbase
    '4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0', // Trust Wallet
  ],
  allWallets: 'SHOW',
});

function WalletProviderInner({ children }) {
  const { address, isConnected, chainId } = useWeb3ModalAccount();
  const { walletProvider } = useWeb3ModalProvider();
  const { disconnect: web3Disconnect } = useDisconnect();
  const { switchNetwork } = useSwitchNetwork();
  const { open } = useWeb3Modal();

  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);

  // Track previous address to detect connection events
  const prevAddressRef = useRef(address);

  // Build ethers BrowserProvider when walletProvider changes
  useEffect(() => {
    let cancelled = false;

    async function setup() {
      if (isConnected && walletProvider) {
        try {
          const bp = new ethers.BrowserProvider(walletProvider);
          if (!cancelled) setProvider(bp);
          const s = await bp.getSigner();
          if (!cancelled) setSigner(s);
        } catch (err) {
          console.error('Failed to create ethers provider:', err);
          if (!cancelled) {
            setProvider(null);
            setSigner(null);
          }
        }
      } else {
        setProvider(null);
        setSigner(null);
      }
    }

    setup();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, walletProvider]);

  // Clear isConnecting once address appears
  useEffect(() => {
    if (address && address !== prevAddressRef.current) {
      setIsConnecting(false);
    }
    prevAddressRef.current = address;
  }, [address]);

  // Also clear isConnecting if modal is closed without connecting
  useEffect(() => {
    if (!isConnected) {
      setIsConnecting(false);
    }
  }, [isConnected]);

  const connect = useCallback(async () => {
    setIsConnecting(true);
    try {
      await open({ view: 'Connect' });
    } catch (err) {
      console.error('Wallet connection failed:', err);
      setIsConnecting(false);
    }
    // Note: isConnecting will be cleared when address appears or modal closes
  }, [open]);

  const disconnect = useCallback(async () => {
    try {
      await web3Disconnect();
    } catch (err) {
      console.error('Disconnect failed:', err);
    }
    // State will be cleared reactively via useWeb3ModalAccount
    setProvider(null);
    setSigner(null);
  }, [web3Disconnect]);

  const switchChain = useCallback(async (targetChainId) => {
    try {
      await switchNetwork(targetChainId);
    } catch (err) {
      console.error('Switch network failed:', err);
      throw err;
    }
  }, [switchNetwork]);

  const value = useMemo(() => ({
    account: address || null,
    chainId: chainId || null,
    provider,
    signer,
    isConnected,
    isConnecting,
    connect,
    disconnect,
    switchChain,
  }), [address, chainId, provider, signer, isConnected, isConnecting, connect, disconnect, switchChain]);

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
}

export function WalletProvider({ children }) {
  return <WalletProviderInner>{children}</WalletProviderInner>;
}

export function useWallet() {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error('useWallet must be used within WalletProvider');
  return ctx;
}

export function formatAddress(address) {
  if (!address) return '';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

export { CHAINS };
